/**
 * Centralized localStorage key constants.
 *
 * Single Responsibility: Define all localStorage keys used across the application.
 * This prevents typos and makes it easy to track/change keys.
 */

// Teacher Auth
export const TEACHER_TOKEN_KEY = 'token';
export const TEACHER_USER_KEY = 'user';

// Student Auth
export const STUDENT_TOKEN_KEY = 'studentToken';
export const STUDENT_KEY = 'student';
export const STUDENT_ENROLLMENT_KEY = 'studentEnrollment';

// Tenant
export const TENANT_KEY = 'tenant';
export const MANAGING_TENANT_KEY = 'managingTenant';

// Backend origin the stored token was minted against (for stale-token detection).
export const TOKEN_ORIGIN_KEY = 'tokenOrigin';

// Grouped keys for bulk cleanup
export const ALL_AUTH_KEYS = [
  TEACHER_TOKEN_KEY,
  TEACHER_USER_KEY,
  STUDENT_TOKEN_KEY,
  STUDENT_KEY,
  STUDENT_ENROLLMENT_KEY,
  TENANT_KEY,
  MANAGING_TENANT_KEY,
];

/**
 * Clear all authentication-related localStorage keys.
 * Use this for logout or 401 handling.
 */
export const clearAllAuthStorage = () => {
  ALL_AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * Reconcile the stored token against the backend origin it must talk to. A token
 * minted against a different backend (e.g. local vs prod — different SECRET_KEY)
 * fails jwt.verify there → 401 → a jarring mid-session logout. On boot we detect
 * the mismatch and clear silently, so the user just sees the login screen instead
 * of the flash-/myprofile → hard-reload bounce.
 *
 * @param {string} currentOrigin - the axios baseURL the app now points at
 * @returns {boolean} true if a stale (wrong-origin) token was cleared
 */
export const reconcileTokenOrigin = (currentOrigin) => {
  const hasToken = localStorage.getItem(TEACHER_TOKEN_KEY) || localStorage.getItem(STUDENT_TOKEN_KEY);
  if (!hasToken) {
    localStorage.setItem(TOKEN_ORIGIN_KEY, currentOrigin);
    return false;
  }
  const storedOrigin = localStorage.getItem(TOKEN_ORIGIN_KEY);
  if (!storedOrigin) {
    // First boot after this shipped — adopt the current origin (the true mint
    // origin is unknown; if the token is actually foreign it'll 401 and the soft
    // handler takes over, but no worse than today).
    localStorage.setItem(TOKEN_ORIGIN_KEY, currentOrigin);
    return false;
  }
  if (storedOrigin !== currentOrigin) {
    clearAllAuthStorage();
    localStorage.setItem(TOKEN_ORIGIN_KEY, currentOrigin);
    return true;
  }
  return false;
};

/** Record the backend origin the current token belongs to. */
export const stampTokenOrigin = (currentOrigin) => {
  localStorage.setItem(TOKEN_ORIGIN_KEY, currentOrigin);
};
